import React, { useState, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader } from './ui/card';
import { Input } from './ui/input';
import {
  Folder,
  FileText,
  ArrowLeft,
  Home,
  Plus,
  Trash2,
  X,
  Minus,
  Square,
  Grip,
  File,
  Image,
  Music,
  Settings,
  Presentation,
  Sheet
} from 'lucide-react';
import { FileSystemNavigator, FileSystemItem } from '../utils/filesystem';
import { useToast } from './ui/use-toast';

interface DraggableFileDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onFileSelect?: (file: FileSystemItem) => void;
  mode?: 'open' | 'save' | 'browse';
  initialPath?: string;
  acceptedTypes?: string[];
  title?: string;
  fileName?: string;
  onFileNameChange?: (name: string) => void;
  onSave?: (folder: FileSystemItem) => void | boolean | Promise<void | boolean>;
  saveOptions?: React.ReactNode;
  // NEW: optional home path (e.g., user's Documents)
  homePath?: string;
}

export const DraggableFileDialog: React.FC<DraggableFileDialogProps> = ({
  isOpen,
  onClose,
  onFileSelect,
  mode = 'open',
  initialPath = 'C:\\',
  acceptedTypes = ['text/html', 'text/plain'],
  title,
  fileName = '',
  onFileNameChange,
  onSave,
  saveOptions,
  homePath
}) => {
  const [navigator] = useState(() => new FileSystemNavigator());
  const [files, setFiles] = useState<FileSystemItem[]>([]);
  const [currentPath, setCurrentPath] = useState(initialPath);
  const [isLoading, setIsLoading] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [position, setPosition] = useState({ x: 100, y: 100 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isMinimized, setIsMinimized] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  
  const dialogRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();

  const loadFiles = async (path: string) => {
    setIsLoading(true);
    try {
      // Ensure the VFS is ready before listing
      await navigator.waitForInitialization();
      await navigator.syncWithVFS();
      const items = navigator.getItemsInPath(path);
      setFiles(items);
    } catch (error) {
      console.error('Error loading files:', error);
      toast({
        title: "Error Loading Files",
        description: "Failed to load files from the directory.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      (async () => {
        // Prefer initialPath; fall back to homePath; then root
        const target = (initialPath || homePath || 'C:\\').replace(/\//g, '\\');
        await navigator.waitForInitialization();
        // Ensure home/initial directory exists (recursively creates parents)
        if (target && target !== 'C:\\') {
          await navigator.createFolder(target);
        }
        setCurrentPath(target);
        await loadFiles(target);
      })();
    }
  }, [isOpen, initialPath, homePath]);

  useEffect(() => {
    if (!isOpen) return;
    // Load whenever path changes
    (async () => {
      await loadFiles(currentPath);
    })();
  }, [isOpen, currentPath]);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget || (e.target as HTMLElement).closest('.drag-handle')) {
      setIsDragging(true);
      const rect = dialogRef.current?.getBoundingClientRect();
      if (rect) {
        setDragOffset({
          x: e.clientX - rect.left,
          y: e.clientY - rect.top
        });
      }
    }
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (isDragging) {
      setPosition({
        x: e.clientX - dragOffset.x,
        y: e.clientY - dragOffset.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragOffset]);

  const navigateToFolder = (folderPath: string) => {
    setCurrentPath(folderPath);
    navigator.navigate(folderPath);
    loadFiles(folderPath);
  };

  const navigateUp = () => {
    const parentPath = navigator.getParentPath(currentPath);
    if (parentPath !== currentPath) {
      setCurrentPath(parentPath);
      navigator.navigate(parentPath);
      loadFiles(parentPath);
    }
  };

  const createFolder = async () => {
    if (!newFolderName.trim()) return;

    try {
      const folderPath = currentPath === 'C:' ? 
        `C:\\${newFolderName}` : 
        `${currentPath}\\${newFolderName}`;
      
      const success = await navigator.createFolder(folderPath);
      
      if (success) {
        setNewFolderName('');
        setShowNewFolder(false);
        loadFiles(currentPath);
        
        // Dispatch filesystem change event
        window.dispatchEvent(new CustomEvent('fileSystemChanged', {
          detail: { path: currentPath }
        }));
        
        toast({
          title: "Folder Created",
          description: `Created folder: ${newFolderName}`,
        });
      } else {
        throw new Error('Failed to create folder');
      }
    } catch (error) {
      console.error('Error creating folder:', error);
      toast({
        title: "Error",
        description: "Failed to create folder.",
        variant: "destructive",
      });
    }
  };

  const deleteFile = async (file: FileSystemItem) => {
    try {
      const success = await navigator.deleteItem(file.path);
      
      if (success) {
        loadFiles(currentPath);
        
        // Dispatch filesystem change event
        window.dispatchEvent(new CustomEvent('fileSystemChanged', {
          detail: { path: currentPath }
        }));
        
        toast({
          title: "Deleted",
          description: `Deleted: ${file.name}`,
        });
      } else {
        throw new Error('Failed to delete file');
      }
    } catch (error) {
      console.error('Error deleting file:', error);
      toast({
        title: "Error",
        description: "Failed to delete file.",
        variant: "destructive",
      });
    }
  };

  const handleFileClick = (file: FileSystemItem) => {
    if (file.type === 'folder') {
      navigateToFolder(file.path);
    } else if (onFileSelect) {
      onFileSelect(file);
      onClose();
    }
  };

  const handleSaveClick = () => {
    const currentFolder: FileSystemItem = {
      id: currentPath,
      name: currentPath.split('\\').pop() || 'C:',
      path: currentPath,
      type: 'folder',
      modified: new Date().toLocaleDateString()
    };
    if (onSave && !isSaving) {
      setIsSaving(true);
      Promise.resolve(onSave(currentFolder))
        .then((result) => {
          if (result !== false) {
            setSavedFlash(true);
            setTimeout(() => setSavedFlash(false), 2000);
            onClose();
          }
        })
        .finally(() => {
          setIsSaving(false);
        });
    }
  };

  const getFileIcon = (file: FileSystemItem, size: number = 20) => {
    if (file.type === 'folder') {
      return <Folder size={size} className="text-primary" />;
    } else {
      switch (file.extension) {
        case 'mslide':
          return <Presentation size={size} className="text-orange-500" />;
        case 'msheet':
          return <Sheet size={size} className="text-green-600" />;
        case 'txt':
        case 'html':
          return <FileText size={size} className="text-foreground" />;
        case 'jpg':
        case 'jpeg':
        case 'png':
        case 'gif':
          return <Image size={size} className="text-accent" />;
        case 'mp3':
        case 'wav':
        case 'flac':
        case 'ogg':
        case 'aac':
        case 'm4a':
          return <Music size={size} className="text-secondary" />;
        case 'bat':
          return <File size={size} className="text-secondary-foreground" />;
        case 'sys':
          return <Settings size={size} className="text-destructive" />;
        default:
          return <File size={size} className="text-muted-foreground" />;
      }
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '';
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] pointer-events-none">
      <div className="absolute inset-0 bg-black/20 pointer-events-auto" onClick={onClose} />
      
      <div
        ref={dialogRef}
        className="absolute pointer-events-auto"
        style={{
          left: position.x,
          top: position.y,
          width: '800px',
          maxWidth: 'calc(100vw - 40px)',
          maxHeight: 'calc(100vh - 40px)'
        }}
      >
        <Card className="shadow-xl border-2">
          {/* Title Bar */}
          <CardHeader 
            className="p-2 bg-gradient-window text-[hsl(var(--window-header-foreground))] cursor-move drag-handle flex flex-row items-center justify-between space-y-0"
            onMouseDown={handleMouseDown}
          >
            <div className="flex items-center gap-2">
              <Grip className="w-4 h-4" />
              <Folder className="w-4 h-4" />
              <span className="text-sm font-medium">
                {title || (mode === 'open' ? 'Open File' : mode === 'save' ? 'Save File' : 'Browse Files')}
              </span>
            </div>
            
            <div className="flex items-center gap-1 text-[hsl(var(--window-header-foreground))]">
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0 bg-[var(--window-control-bg)] text-current hover:bg-[var(--window-control-bg-hover)]"
                onClick={() => setIsMinimized(!isMinimized)}
              >
                <Minus size={12} strokeWidth={2.5} />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0 bg-[var(--window-control-bg)] text-current hover:bg-[var(--window-control-bg-hover)]"
                onClick={() => setIsMinimized(!isMinimized)}
              >
                <Square size={10} strokeWidth={2.25} />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0 bg-[var(--window-control-bg)] text-current hover:bg-destructive hover:text-destructive-foreground"
                onClick={onClose}
              >
                <X size={12} strokeWidth={2.25} />
              </Button>
            </div>

          </CardHeader>

          {!isMinimized && (
            <CardContent className="p-0">
              {/* File Name Input for Save Mode */}
              {mode === 'save' && (
                <div className="p-3 border-b bg-muted/50">
                  <div className="text-sm font-medium mb-2">File name:</div>
                  <Input
                    value={fileName}
                    onChange={(e) => onFileNameChange?.(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleSaveClick();
                      }
                    }}
                    placeholder="Enter file name"
                    className="w-full"
                  />
                  {savedFlash && (
                    <div className="mt-1 text-xs text-green-600">*saved</div>
                  )}
                  {saveOptions && <div className="mt-3">{saveOptions}</div>}
                </div>
              )}

              {/* Navigation Bar */}
              <div className="flex items-center gap-2 p-2 border-b bg-muted/30">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={navigateUp}
                  disabled={currentPath === 'C:\\'}
                >
                  <ArrowLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPath(homePath || 'C:\\')}
                >
                  <Home className="w-4 h-4" />
                </Button>
                <div className="flex-1 text-sm text-muted-foreground font-mono bg-background px-2 py-1 rounded border">
                  {currentPath}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowNewFolder(true)}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  New Folder
                </Button>
              </div>

              {/* New Folder Input */}
              {showNewFolder && (
                <div className="flex items-center gap-2 p-3 border-b bg-yellow-50 dark:bg-yellow-900/20">
                  <Input
                    value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                    placeholder="Folder name"
                    className="flex-1"
                    onKeyDown={(e) => e.key === 'Enter' && createFolder()}
                    autoFocus
                  />
                  <Button size="sm" onClick={createFolder}>
                    Create
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => {
                      setShowNewFolder(false);
                      setNewFolderName('');
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              )}

              {/* File List */}
              <div className="h-96 overflow-auto">
                {isLoading ? (
                  <div className="flex items-center justify-center h-32">
                    <div className="text-sm text-muted-foreground">Loading...</div>
                  </div>
                ) : (
                  <div className="space-y-0">
                    {files.map((file, index) => (
                      <div
                        key={`${file.path}-${index}`}
                        className="flex items-center gap-3 p-3 hover:bg-muted cursor-pointer group border-b border-border/30"
                        onClick={() => handleFileClick(file)}
                      >
                        {getFileIcon(file)}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">{file.name}</div>
                          <div className="text-xs text-muted-foreground">
                            {file.type !== 'folder' && file.size && formatFileSize(parseInt(file.size))}
                          </div>
                        </div>
                        <div className="text-xs text-muted-foreground whitespace-nowrap">
                          {file.modified}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="opacity-0 group-hover:opacity-100 h-8 w-8 p-0"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteFile(file);
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                    {files.length === 0 && (
                      <div className="text-center py-12 text-muted-foreground">
                        <Folder className="w-16 h-16 mx-auto mb-4 opacity-50" />
                        <div className="text-lg font-medium">This folder is empty</div>
                        <div className="text-sm">Add files or create new folders to get started</div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-2 p-3 border-t bg-muted/30">
                <Button variant="outline" onClick={onClose}>
                  Cancel
                </Button>
                {mode === 'save' && (
                  <Button onClick={handleSaveClick} disabled={isSaving}>
                    Save Here
                  </Button>
                )}
                {mode === 'open' && (
                  <Button 
                    onClick={() => {
                      const documentsFolder = files.find(f => f.name === 'Documents' && f.type === 'folder');
                      if (documentsFolder && onFileSelect) {
                        onFileSelect(documentsFolder);
                      }
                    }}
                    disabled={!files.find(f => f.name === 'Documents' && f.type === 'folder')}
                  >
                    Open
                  </Button>
                )}
              </div>
            </CardContent>
          )}
        </Card>
      </div>
    </div>
  );
};